/**
 * Storage — namespaced JSON wrapper around localStorage.
 *
 * Two namespaces are reserved:
 *   - 'platform'        — shared across all games (player name, language, mute)
 *   - <manifest.id>     — per-game settings, scores, achievements
 *
 * Survives `localStorage` being unavailable (Safari private mode, etc.) by
 * falling back to an in-memory map.
 */
export class Storage {
  /** @param {string} namespace */
  constructor(namespace) {
    this.namespace = namespace;
    this._mem = new Map();
    this._hasLs = (function probe() {
      try {
        const k = '__platform_probe__';
        window.localStorage.setItem(k, '1');
        window.localStorage.removeItem(k);
        return true;
      } catch (_) {
        return false;
      }
    })();
  }

  _key(key) {
    return 'platform/' + this.namespace + '/' + key;
  }

  get(key, fallback) {
    const k = this._key(key);
    if (this._hasLs) {
      const raw = window.localStorage.getItem(k);
      if (raw == null) return fallback;
      try {
        return JSON.parse(raw);
      } catch (_) {
        return fallback;
      }
    }
    return this._mem.has(k) ? this._mem.get(k) : fallback;
  }

  set(key, value) {
    const k = this._key(key);
    if (this._hasLs) {
      try {
        window.localStorage.setItem(k, JSON.stringify(value));
        return;
      } catch (_) { /* fall through to memory */ }
    }
    this._mem.set(k, value);
  }

  remove(key) {
    const k = this._key(key);
    if (this._hasLs) {
      try { window.localStorage.removeItem(k); } catch (_) { /* ignore */ }
    }
    this._mem.delete(k);
  }
}

/** Shared platform-wide preferences (name, language, mute, last room). */
export const platformStorage = new Storage('platform');
